import type { House, Request, Room, RoomOccupancyRow } from '@dia/db';

/**
 * The Habitaciones grid, worked out on the phone.
 *
 * The database says who is in which room (the room_occupancy view, migration
 * 016). What a cleaner needs on top of that is what to do next, and that is
 * arithmetic on the stay dates rather than a column somebody has to remember
 * to set. Nothing here is stored: a room that stops needing a turnover stops
 * needing one because the calendar moved, not because anybody ticked a box.
 */

/** Occupied now, waiting for its changeover, or ready and empty. */
export type RoomState = 'occupied' | 'turnover' | 'empty';

export type RoomCard = {
  room: Room;
  state: RoomState;
  /** The people in it now, or the people who just left if it needs a turnover. */
  occupants: RoomOccupancyRow[];
  /** Somebody asked for housekeeping in this room and it is still open. */
  serviceDue: boolean;
  /** The room holds more people than its own beds, so the futon is made up. */
  futonInUse: boolean;
};

export type HouseGrid = { house: House; cards: RoomCard[] };

function isLive(row: RoomOccupancyRow, today: string): boolean {
  if (row.stay_status === 'cancelled' || row.stay_status === 'departed') return false;
  return row.arrives_on <= today && today <= row.departs_on;
}

function hasLeft(row: RoomOccupancyRow, today: string): boolean {
  if (row.stay_status === 'cancelled') return false;
  return row.stay_status === 'departed' || row.departs_on < today;
}

/** One room's card. Exported on its own because the tests read better that way. */
export function cardFor(
  room: Room,
  occupancy: RoomOccupancyRow[],
  requests: Request[],
  today: string,
): RoomCard {
  const mine = occupancy.filter((row) => row.room_id === room.id);
  const live = mine.filter((row) => isLive(row, today));
  const left = mine.filter((row) => hasLeft(row, today));

  const state: RoomState = live.length > 0 ? 'occupied' : left.length > 0 ? 'turnover' : 'empty';
  const occupants = (live.length > 0 ? live : left).sort((a, b) =>
    a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name),
  );

  return {
    room,
    state,
    occupants,
    serviceDue: requests.some(
      (r) =>
        r.room_id === room.id &&
        r.type === 'housekeeping' &&
        ['new', 'seen', 'in_progress'].includes(r.status),
    ),
    futonInUse: room.extra_sleeps > 0 && live.length > room.base_sleeps,
  };
}

/**
 * The whole grid, house by house in seed order. Houses with no bedrooms are
 * left out entirely rather than drawn as an empty heading.
 */
export function buildGrid(input: {
  rooms: Room[];
  houses: House[];
  occupancy: RoomOccupancyRow[];
  requests: Request[];
  today: string;
}): HouseGrid[] {
  const { rooms, houses, occupancy, requests, today } = input;

  return [...houses]
    .sort((a, b) => a.sort - b.sort)
    .map((house) => ({
      house,
      cards: rooms
        .filter((r) => r.claimable && r.house_id === house.id)
        .sort((a, b) => a.sort - b.sort || a.slug.localeCompare(b.slug))
        .map((room) => cardFor(room, occupancy, requests, today)),
    }))
    .filter((group) => group.cards.length > 0);
}

/** The one line at the top: how much of the island is in use right now. */
export function gridSummary(grid: HouseGrid[]): {
  occupied: number;
  turnover: number;
  empty: number;
  people: number;
} {
  const cards = grid.flatMap((g) => g.cards);
  return {
    occupied: cards.filter((c) => c.state === 'occupied').length,
    turnover: cards.filter((c) => c.state === 'turnover').length,
    empty: cards.filter((c) => c.state === 'empty').length,
    people: cards.filter((c) => c.state === 'occupied').reduce((n, c) => n + c.occupants.length, 0),
  };
}

/** What the island sleeps in all, from the rows rather than from a constant. */
export function islandSleeps(rooms: Room[]): number {
  return rooms.filter((r) => r.claimable).reduce((n, r) => n + r.max_sleeps, 0);
}
